import { injectIf } from '@fastre/core/helperFunctions/object'
import {
    MarketingItemCategories,
    MarketingItemSchema,
    UpdateMarketingItemSchema,
} from '@fastre/core/schemas/marketingPackage'
import { zodResolver } from '@hookform/resolvers/zod'
import EditRounded from '@mui/icons-material/EditRounded'
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItemButton,
    ListItemDecorator,
    Modal,
    ModalClose,
    ModalDialog,
    Option,
    Select,
    Stack,
    Typography,
} from '@mui/joy'
import List from '@mui/material/List'
import { useApi } from 'api'
import { useMarketingItemsApi } from 'apiProviders'
import { MultipleAgentInput } from 'components/agentInput'
import { SlotInput, SlotWrapper } from 'components/input'
import { dontCloseOnBackgroundClick } from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { useMaybeState } from 'helperFunctions/react'
import { useOrgId } from 'helpers'
import { groupBy, prop, sortBy } from 'ramda'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { v4 as uuid } from 'uuid'

const EditItemModal = ({ item, close }: { item: MarketingItemSchema; close: () => void }) => {
    const isNew = item.itemId == undefined
    const api = useApi()
    const showSnack = useShowSnack()
    const itemsApi = useMarketingItemsApi()
    const orgId = useOrgId()

    const { register, control, handleSubmit, formState, getValues, setValue } =
        useForm<UpdateMarketingItemSchema>({
            defaultValues: {
                ...injectIf(isNew, {
                    parentId: orgId!,
                    itemId: uuid(),
                }),
                ...item,
            },
            resolver: zodResolver(UpdateMarketingItemSchema),
        })

    const [loading, setLoading] = useState(false)

    if (Object.keys(formState.errors).length > 0) {
        console.log(formState.errors)
    }

    const onSubmit: SubmitHandler<MarketingItemSchema> = async data => {
        setLoading(true)
        try {
            await api.post(`/marketingitem/addupdate`, data).then(prop('data'))
            itemsApi.refresh()
            close()
            showSnack('Marketing item saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving marketing item', 'danger')
        } finally {
            setLoading(false)
        }
    }

    return (
        <ModalDialog minWidth={600}>
            <DialogTitle>{isNew ? 'Create' : 'Edit'} Marketing Item</DialogTitle>
            <ModalClose />
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
            >
                <DialogContent>
                    <Controller
                        name="itemCategory"
                        control={control}
                        render={field => (
                            <SlotWrapper
                                {...field}
                                label="Category"
                            >
                                <Select onChange={(e, value: any) => setValue('itemCategory', value)}>
                                    {sortBy(x => x, MarketingItemCategories).map(cat => (
                                        <Option value={cat}>{cat}</Option>
                                    ))}
                                </Select>
                            </SlotWrapper>
                        )}
                    />
                    <Controller
                        name="userIds"
                        control={control}
                        render={field => (
                            <Box sx={{ mt: 2 }}>
                                <SlotWrapper
                                    label="Available To"
                                    {...field}
                                >
                                    <MultipleAgentInput
                                        filter={prop('salesAgent')}
                                        {...field.field}
                                        value={field.field.value ?? []}
                                        placeholder="All Agents"
                                    />
                                </SlotWrapper>
                            </Box>
                        )}
                    />
                    <Controller
                        name="itemName"
                        control={control}
                        render={field => (
                            <SlotInput
                                label="Name"
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="itemPrice"
                        control={control}
                        render={field => (
                            <SlotInput
                                label="Price"
                                type="number"
                                startDecorator="$"
                                {...field}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        type="submit"
                        loading={loading}
                    >
                        Save
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={close}
                    >
                        Cancel
                    </Button>
                    {!isNew && (
                        <Box
                            sx={{
                                flexGrow: 1,
                            }}
                        >
                            <Button
                                color="danger"
                                variant="outlined"
                                loading={loading}
                                onClick={async () => {
                                    setLoading(true)
                                    await api.post('/marketingitem/delete', {
                                        parentId: orgId,
                                        itemId: item.itemId,
                                    })
                                    await itemsApi.refresh()
                                    close()
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                    )}
                </DialogActions>
            </form>
        </ModalDialog>
    )
}

const MarketingItemsSettings = () => {
    const itemsApi = useMarketingItemsApi()
    const [maybeEditItem, setEditItem] = useMaybeState<MarketingItemSchema>()

    return (
        <>
            <Box>
                <Stack spacing={1}>
                    {itemsApi.maybeData
                        .map(items => {
                            // Group items by itemCategory and sort categories alphabetically
                            const groupedAndSortedCategories = Object.entries(
                                groupBy((item: MarketingItemSchema) => item.itemCategory, items),
                            ).sort(([catA], [catB]) => catA.localeCompare(catB))

                            // Render grouped and sorted items by category
                            return groupedAndSortedCategories.map(([cat, items]) => (
                                <Box sx={{ maxWidth: 800, margin: 'auto', mt: 1 }}>
                                    <Stack
                                        key={cat}
                                        spacing={1}
                                    >
                                        <Typography
                                            sx={{
                                                textTransform: 'uppercase',
                                                mb: 1,
                                                fontWeight: 'bold',
                                                fontSize: '0.875rem', // Match the font size of ListSubheader
                                                lineHeight: 1,
                                                color: 'text.secondary', // Subtle text color to reduce brightness
                                                padding: '4px 8px', // Adds padding for better spacing
                                                borderRadius: '4px', // Optional rounded corners
                                            }}
                                        >
                                            {cat}
                                        </Typography>
                                        <List
                                            component="nav"
                                            sx={{ maxWidth: 320, borderRadius: 'sm' }}
                                        >
                                            {items
                                                .sort((a, b) => a.itemName.localeCompare(b.itemName))
                                                .map(item => (
                                                    <>
                                                        <ListItemButton
                                                            key={item.itemId}
                                                            onClick={() => setEditItem(item)}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center', // Aligns content vertically centered
                                                                p: 1, // Padding for internal spacing
                                                                borderRadius: '8px', // Rounded corners
                                                                mb: 1, // Bottom margin for spacing
                                                                border: '0.5px solid transparent', // Transparent border to maintain size
                                                                transition:
                                                                    'background-color 0.3s, transform 0.3s, border-color 0.3s', // Smooth transition for hover effects
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Slight background change on hover
                                                                    border: '0.5px solid', // Border appears on hover
                                                                    borderColor: 'primary.main',
                                                                    '& *': {
                                                                        filter: 'brightness(1.1)', // Brightens all child elements on hover
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {/* Item details: name and price */}
                                                            <ListItemDecorator
                                                                sx={{
                                                                    minWidth: 'auto',
                                                                    mr: 1,
                                                                    display: 'flex',
                                                                    verticalAlignItems: 'bottom',
                                                                    alignItems: 'center',
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <EditRounded />
                                                                </Box>
                                                                <Typography>{item.itemName}</Typography>
                                                            </ListItemDecorator>

                                                            <Typography sx={{ fontWeight: 500 }}>
                                                                ${item.itemPrice.toFixed(2)}
                                                            </Typography>
                                                        </ListItemButton>
                                                        {/* 
                                            <ButtonSheet
                                                key={item.itemId}
                                                variant="outlined"
                                                onClick={() => setEditItem(item)}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    maxWidth: 700,
                                                }}
                                            >
                                                    <Box>{item.itemName}</Box>
                                                    <Box>${item.itemPrice}</Box>
                                                </ButtonSheet> */}
                                                    </>
                                                ))}
                                        </List>
                                    </Stack>
                                </Box>
                            ))
                        })
                        .orSome([])}

                    <Box>
                        <Button
                            onClick={() =>
                                setEditItem({
                                    itemCategory: MarketingItemCategories[0],
                                    itemName: '',
                                    itemPrice: 0,
                                })
                            }
                            sx={{ mt: 2 }}
                            variant="outlined"
                        >
                            Add Item
                        </Button>
                    </Box>
                </Stack>
                <Modal
                    open={maybeEditItem.isSome()}
                    onClose={dontCloseOnBackgroundClick(() => setEditItem(undefined))}
                >
                    {maybeEditItem
                        .map(item => (
                            <EditItemModal
                                item={item}
                                close={() => setEditItem(undefined)}
                            />
                        ))
                        .orSome(<></>)}
                </Modal>
            </Box>
        </>
    )
}

export default MarketingItemsSettings
